import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    IconButton,
    Alert,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Tooltip,
    Divider,
    Card,
    CardContent,
    InputAdornment
} from '@mui/material';
import {
    ContentCopy,
    Refresh,
    Share,
    PersonAdd,
    CheckCircle,
    Email,
    Link as LinkIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';

interface Referral {
    _id: string;
    name: string;
    email: string;
    userType: string;
    subscription: string;
    referredAt: string;
    createdAt: string;
}

const InvitationLinks: React.FC = () => {
    const { user } = useAuthStore();
    const [referralCode, setReferralCode] = useState('');
    const [referralLink, setReferralLink] = useState('');
    const [referrals, setReferrals] = useState<Referral[]>([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState('');
    const [copied, setCopied] = useState(false);
    const [copiedLink, setCopiedLink] = useState(false);

    useEffect(() => {
        fetchReferralData();
    }, []);

    const fetchReferralData = async () => {
        try {
            setLoading(true);
            setError('');

            // Fetch referral code
            const codeResponse = await api.get('/auth/my-referral-code');
            setReferralCode(codeResponse.data.referralCode);
            setReferralLink(codeResponse.data.referralLink);

            // Fetch referrals
            const referralsResponse = await api.get('/auth/my-referrals');
            setReferrals(referralsResponse.data.referrals || []);
        } catch (err: any) {
            if (err.response?.status === 403 || err.message?.includes('403')) {
                setError('Invitation links are only available for lawyers and estate executors.');
            } else {
                setError(err.response?.data?.error || err.message || 'Failed to load referral data');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleRegenerateCode = async () => {
        try {
            setGenerating(true);
            setError('');
            const response = await api.post('/auth/generate-referral-code', {});
            setReferralCode(response.data.referralCode);
            setReferralLink(response.data.referralLink);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Failed to generate new code');
        } finally {
            setGenerating(false);
        }
    };

    const copyToClipboard = async (text: string, isLink: boolean = false) => {
        try {
            await navigator.clipboard.writeText(text);
            if (isLink) {
                setCopiedLink(true);
                setTimeout(() => setCopiedLink(false), 2000);
            } else {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Join Wills',
                    text: 'I\'ve invited you to create your will. Use my referral link to get started!',
                    url: referralLink
                });
            } catch (err) {
                // User cancelled or share failed
                console.log('Share cancelled');
            }
        } else {
            // Fallback to copy
            copyToClipboard(referralLink, true);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getUserTypeLabel = (type: string) => {
        switch (type) {
            case 'EO': return 'Estate Owner';
            case 'EL': return 'Estate Lawyer';
            case 'EX': return 'Estate Executor';
            default: return type;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error && !referralCode) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning" sx={{ maxWidth: 600, mx: 'auto' }}>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h4" fontWeight="bold" gutterBottom>
                    Invitation Links
                </Typography>
                <Typography color="text.secondary">
                    Share your referral link to invite clients and track who has signed up.
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Referral Code Card */}
            <Card
                elevation={0}
                sx={{
                    mb: 4,
                    background: 'linear-gradient(135deg, #6DB4A4 0%, #5A9B8B 100%)',
                    color: 'white',
                    borderRadius: 3
                }}
            >
                <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PersonAdd sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Your Referral Code
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 3 }}>
                        <Paper
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                px: 3,
                                py: 1.5,
                                borderRadius: 2,
                                backgroundColor: 'rgba(255,255,255,0.95)'
                            }}
                        >
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{
                                    color: '#222',
                                    letterSpacing: 2,
                                    fontFamily: 'monospace'
                                }}
                            >
                                {referralCode}
                            </Typography>
                            <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                                <IconButton
                                    onClick={() => copyToClipboard(referralCode)}
                                    sx={{ ml: 1, color: copied ? '#4caf50' : '#666' }}
                                >
                                    {copied ? <CheckCircle /> : <ContentCopy />}
                                </IconButton>
                            </Tooltip>
                        </Paper>

                        <Button
                            variant="outlined"
                            startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
                            onClick={handleRegenerateCode}
                            disabled={generating}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Regenerate
                        </Button>
                    </Box>

                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 3 }} />

                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        Share this link with your clients:
                    </Typography>

                    <TextField
                        fullWidth
                        value={referralLink}
                        InputProps={{
                            readOnly: true,
                            startAdornment: (
                                <InputAdornment position="start">
                                    <LinkIcon sx={{ color: '#666' }} />
                                </InputAdornment>
                            ),
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Tooltip title={copiedLink ? 'Copied!' : 'Copy link'}>
                                        <IconButton onClick={() => copyToClipboard(referralLink, true)}>
                                            {copiedLink ? <CheckCircle color="success" /> : <ContentCopy />}
                                        </IconButton>
                                    </Tooltip>
                                </InputAdornment>
                            ),
                            sx: {
                                backgroundColor: 'white',
                                borderRadius: 2,
                                '& input': {
                                    color: '#333',
                                    fontSize: '0.95rem'
                                }
                            }
                        }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                        <Button
                            variant="contained"
                            startIcon={<Share />}
                            onClick={handleShare}
                            sx={{
                                backgroundColor: 'white',
                                color: '#5A9B8B',
                                '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.9)'
                                }
                            }}
                        >
                            Share Link
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<Email />}
                            onClick={() => window.open(`mailto:?subject=Create Your Will&body=I've invited you to create your will. Use my referral link to get started: ${encodeURIComponent(referralLink)}`, '_blank')}
                            sx={{
                                color: 'white',
                                borderColor: 'rgba(255,255,255,0.5)',
                                '&:hover': {
                                    borderColor: 'white',
                                    backgroundColor: 'rgba(255,255,255,0.1)'
                                }
                            }}
                        >
                            Send via Email
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Stats */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
                <Paper sx={{ p: 3, flex: 1, minWidth: 200, borderRadius: 2 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                        Total Referrals
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" color="primary">
                        {referrals.length}
                    </Typography>
                </Paper>
                <Paper sx={{ p: 3, flex: 1, minWidth: 200, borderRadius: 2 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                        This Month
                    </Typography>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: '#6DB4A4' }}>
                        {referrals.filter(r => {
                            const date = new Date(r.referredAt || r.createdAt);
                            const now = new Date();
                            return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                        }).length}
                    </Typography>
                </Paper>
                <Paper sx={{ p: 3, flex: 1, minWidth: 200, borderRadius: 2 }}>
                    <Typography color="text.secondary" variant="body2" gutterBottom>
                        Your Role
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                        {getUserTypeLabel(user?.userType || 'EO')}
                    </Typography>
                </Paper>
            </Box>

            {/* Referrals Table */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{ p: 3, borderBottom: '1px solid #eee' }}>
                    <Typography variant="h6" fontWeight="bold">
                        Referred Users
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        People who signed up using your referral link
                    </Typography>
                </Box>

                {referrals.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <PersonAdd sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            No referrals yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Share your referral link to start inviting clients.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                                    <TableCell><strong>Name</strong></TableCell>
                                    <TableCell><strong>Email</strong></TableCell>
                                    <TableCell><strong>Type</strong></TableCell>
                                    <TableCell><strong>Plan</strong></TableCell>
                                    <TableCell><strong>Joined</strong></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {referrals.map((referral) => (
                                    <TableRow key={referral._id} hover>
                                        <TableCell>{referral.name}</TableCell>
                                        <TableCell>{referral.email}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={getUserTypeLabel(referral.userType)}
                                                size="small"
                                                sx={{
                                                    backgroundColor: referral.userType === 'EO' ? '#e3f2fd' : '#f3e5f5',
                                                    color: referral.userType === 'EO' ? '#1976d2' : '#7b1fa2'
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={referral.subscription}
                                                size="small"
                                                color={
                                                    referral.subscription === 'PLUS' ? 'success' :
                                                        referral.subscription === 'PRO' ? 'primary' : 'default'
                                                }
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>{formatDate(referral.referredAt || referral.createdAt)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>
        </Box>
    );
};

export default InvitationLinks;
